<template>
    <Head><title>Terms &amp; Consent</title></Head>
    <div class="flex flex-col min-h-screen overflow-y-auto h-screen bg-valence-neutral-light text-valence-grey-800 safe-area-top">
        <div class="fade-in grow justify-center gap-5 md:gap-8 flex flex-col items-center py-4 md:py-10">
            <div class="flex flex-col gap-4 justify-around text-center max-w-sm mx-auto px-4">
                <div class="flex flex-col pb-5 justify-center">
                    <img :src="Logo" alt="Valence logo" class="mx-auto h-16 w-auto" />
                </div>
            </div>
            <div class="flex flex-col gap-4 items-center max-w-md w-full px-4">
                <div class="text-center mb-4">
                    <h2 class="title-body mb-2">{{ copy.heading }}</h2>
                    <p class="body-small text-gray-600">{{ copy.body }}</p>
                </div>

                <div class="max-w-md mx-auto w-full">
                    <TermsConsent v-model:terms="terms" :terms-content="terms_content" :modal="modal_terms" @accept="acceptTerms" />
                </div>

                <p v-if="error" class="text-error font-medium">{{ error.message }}</p>
                <LoadingSpinner v-if="isFetching" class="w-10 h-10" />
                <BaseButton v-else-if="!modal_terms" theme="primary" class="w-full py-4 rounded-xl font-semibold" :disabled="!terms" @click="acceptTerms">Continue</BaseButton>
            </div>
        </div>
    </div>
</template>

<script setup>
import { Head } from "@inertiajs/vue3";
import Logo from "~img/color-logo.svg";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import TermsConsent from "~vue/components/TermsConsent.vue";
import { useStatusBar } from "~vue/composables/useStatusBar";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { computed, ref } from "vue";

useStatusBar();

const props = defineProps({
    terms_content: { type: String, default: null },
    terms_document_id: { type: String, required: true },
    next_url: { type: String, default: "/" },
    modal_terms: { type: Boolean, default: false },
    reconsent_reason: { type: String, default: "first_time" },
});

// The heading carries the re-consent framing here, so the shared TermsConsent component
// is deliberately not passed the reason (its inline notice would double the message).
const RECONSENT_COPY = {
    first_time: { heading: "Terms of Consent Required", body: "Please accept the terms to continue." },
    updated: {
        heading: "Our Terms Have Been Updated",
        body: "The terms have been updated since you last accepted them. Please review and accept them to continue.",
    },
    grandfathered: {
        heading: "Please Re-accept the Terms",
        body: "We've improved our records around terms acceptance. Please re-accept the terms to continue.",
    },
};

const copy = computed(() => RECONSENT_COPY[props.reconsent_reason] ?? RECONSENT_COPY.first_time);

const terms = ref(false);

const { data, error, execute, isFetching } = useFetch("/accounts/terms-consent/", { immediate: false })
    .post(() => ({ terms: terms.value, terms_document_id: props.terms_document_id, next: props.next_url }))
    .json();

async function acceptTerms() {
    if (!terms.value) {
        return;
    }
    await execute();
    if (!error.value) {
        globalThis.location.href = data.value?.redirect ?? props.next_url;
    }
}
</script>
